// Purchase requests (docs/plans/purchase-requests.md): the words for a request's state, and
// the approved lines grouped into one draft purchase order per supplier.
import type { PurchaseRequestRow, RequestLineLeft } from '@ims/data'
import type { DraftPlan } from './reorder.ts'

type Tone = 'neutral' | 'warning' | 'success' | 'info' | 'danger'

export interface RequestStanding {
  request: Pick<PurchaseRequestRow, 'status'>
  lineCount: number
  doneLines: number
  startedLines: number
}

/** The pill: what a person decided, and for an approved request how much is on orders. */
export function requestState(r: RequestStanding): { label: string; tone: Tone } {
  switch (r.request.status) {
    case 'draft':
      return { label: 'Draft', tone: 'neutral' }
    case 'submitted':
      return { label: 'Waiting approval', tone: 'warning' }
    case 'rejected':
      return { label: 'Rejected', tone: 'danger' }
    case 'closed':
      return { label: 'Closed', tone: 'neutral' }
    case 'cancelled':
      return { label: 'Cancelled', tone: 'neutral' }
    case 'approved':
      if (r.lineCount > 0 && r.doneLines >= r.lineCount) return { label: 'Ordered', tone: 'info' }
      return r.startedLines > 0
        ? { label: 'Part ordered', tone: 'info' }
        : { label: 'To order', tone: 'success' }
  }
}

/** Whether something is still to be ordered from it. */
export const hasLeftToOrder = (r: RequestStanding) =>
  r.request.status === 'approved' && r.doneLines < r.lineCount

export interface RequestOrderGroup {
  /** Null: no supplier chosen yet. */
  supplierId: string | null
  lines: RequestLineLeft[]
}

/**
 * The lines still to order, by supplier: the one chosen for the line's product here, else
 * the product's usual supplier. Written-in lines (no product yet) are kept apart: they are
 * matched to a product before they can be ordered.
 */
export function groupRequestLines(
  lines: readonly RequestLineLeft[],
  chosen: Readonly<Record<string, string>>,
  suppliers: ReadonlySet<string>,
): { groups: RequestOrderGroup[]; unmatched: RequestLineLeft[] } {
  const groups = new Map<string | null, RequestOrderGroup>()
  const unmatched: RequestLineLeft[] = []
  for (const l of lines) {
    if (!l.product) {
      unmatched.push(l)
      continue
    }
    const usual =
      l.product.supplier_id && suppliers.has(l.product.supplier_id) ? l.product.supplier_id : null
    const id = chosen[l.product.id] ?? usual
    let g = groups.get(id)
    if (!g) groups.set(id, (g = { supplierId: id, lines: [] }))
    g.lines.push(l)
  }
  // Groups with a supplier first, in the order met; the one without last.
  const list = [...groups.values()].sort((a, b) =>
    a.supplierId === null ? 1 : b.supplierId === null ? -1 : 0,
  )
  return { groups: list, unmatched }
}

/**
 * One draft per supplier. Each request line is its own order line (pointing back at it), so
 * every request knows how much of it is ordered; the cost is the product's cost price, else
 * the request's estimate.
 */
export function requestDraftPlans(
  groups: readonly RequestOrderGroup[],
  skip: ReadonlySet<string> = new Set(),
): DraftPlan[] {
  return groups.flatMap((g) => {
    if (!g.supplierId) return []
    const lines = g.lines
      .filter((l) => !skip.has(l.line.id) && l.leftMilli > 0)
      .map((l) => ({
        productId: l.product!.id,
        qtyMilli: l.leftMilli,
        unitCostPaisa: l.product!.cost_price_paisa ?? l.line.est_cost_paisa,
        requestLineId: l.line.id,
      }))
    return lines.length ? [{ supplierId: g.supplierId, lines }] : []
  })
}
